import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { AuthResponse, LoginRequest, SignUpRequest, User } from "../types/api";
import { apiClient } from "../utils/api";
import { modesRegistryQueryKey } from "./useModesRegistry";
import {
  MixpanelEvents,
  initMixpanel,
  resetMixpanel,
  trackEvent,
} from "../utils/mixpanel";

/** Safe internal path from ?next= for post-login redirect (open redirect safe). */
export function getPostAuthRedirectPath(): string {
  if (typeof window === "undefined") return "/";
  const next = new URLSearchParams(window.location.search).get("next");
  if (next && next.startsWith("/") && !next.startsWith("//")) {
    return next.split("?")[0];
  }
  return "/";
}

export const useSignUp = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SignUpRequest) => apiClient.signUp(data),
    onSuccess: (data: AuthResponse) => {
      queryClient.setQueryData(["user"], data.user);
      // Initialize Mixpanel and track sign up
      if (data.user) {
        initMixpanel(data.user);
        trackEvent(MixpanelEvents.SIGN_UP, {
          email: data.user.email,
          username: data.user.username,
        });
      }
      void queryClient.prefetchQuery({
        queryKey: modesRegistryQueryKey,
        queryFn: () => apiClient.getModesRegistry(),
      });
      queryClient.invalidateQueries({ queryKey: ["userCredits"] });
      navigate(getPostAuthRedirectPath());
    },
    onError: (error: any) => {
      trackEvent(MixpanelEvents.SIGN_UP_FAILED, {
        error_message: error?.response?.data?.detail || "Unknown error",
        error_detail: error?.response?.data,
      });
    },
  });
};

export const useLogin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LoginRequest) => apiClient.login(data),
    onSuccess: (data: AuthResponse) => {
      queryClient.setQueryData(["user"], data.user);
      // Initialize Mixpanel and track login
      if (data.user) {
        initMixpanel(data.user);
        trackEvent(MixpanelEvents.LOGIN, {
          email: data.user.email,
          username: data.user.username,
        });
      }
      void queryClient.prefetchQuery({
        queryKey: modesRegistryQueryKey,
        queryFn: () => apiClient.getModesRegistry(),
      });
      queryClient.invalidateQueries({ queryKey: ["userCredits"] });
      navigate(getPostAuthRedirectPath());
    },
    onError: (error: any) => {
      trackEvent(MixpanelEvents.LOGIN_FAILED, {
        error_message: error?.response?.data?.detail || "Unknown error",
        error_detail: error?.response?.data,
      });
    },
  });
};

export const useLogout = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      // Track logout before clearing
      trackEvent(MixpanelEvents.LOGOUT);
      resetMixpanel();
      apiClient.logout();
      try {
        localStorage.clear();
      } catch {
        /* private mode / blocked storage */
      }
      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.clear();
      navigate("/login");
    },
  });
};

export const useUser = () => {
  return useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const token = localStorage.getItem("access_token");
      if (!token) {
        // Clear user data if no token
        apiClient.logout();
        return null;
      }

      // First try to get user data from localStorage
      const storedUser = apiClient.getUserData();
      if (!storedUser) {
        return null;
      }

      // Refresh credits only when we have a session (token + stored user)
      try {
        const profile = await apiClient.getProfile();
        const updatedUser = {
          ...storedUser,
          available_credits: profile.credit,
          no_restrictions: profile.no_restrictions,
          profile: {
            ...storedUser.profile,
            available_credits: profile.credit,
            no_restrictions: profile.no_restrictions,
            use_free_content:
              profile.use_free_content ?? storedUser.profile.use_free_content,
          },
        };
        apiClient.setUserData(updatedUser);
        initMixpanel(updatedUser);
        return updatedUser;
      } catch {
        initMixpanel(storedUser);
        return storedUser;
      }
    },
    enabled:
      !!localStorage.getItem("access_token") && !!apiClient.getUserData(),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    initialData: () => {
      // Return stored user data immediately for instant UI
      const user = apiClient.getUserData();
      // Initialize Mixpanel if user exists (for session restoration)
      if (user) {
        initMixpanel(user);
      }
      return user;
    },
  });
};

export const useUserCredits = () => {
  const accessToken = localStorage.getItem("access_token");
  
  return useQuery({
    queryKey: ["userCredits"],
    queryFn: () => apiClient.getProfile(),
    enabled: !!accessToken, // Only fetch when user is logged in
    refetchInterval: (query) => {
      // Only refetch if there's an access token
      return !!localStorage.getItem("access_token") ? 30000 : false;
    },
  });
};

/** After a party mode charges a credit on start, refresh profile credit + cached user. */
export function invalidateUserSessionCredits(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ["userCredits"] });
  void queryClient.invalidateQueries({ queryKey: ["user"] });
}

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<User>) => apiClient.updateProfile(data),
    onSuccess: (data: User) => {
      queryClient.setQueryData(["user"], data);
      queryClient.invalidateQueries({ queryKey: ["userCredits"] });
      // Track profile update
      trackEvent(MixpanelEvents.PROFILE_UPDATED, {
        updated_fields: Object.keys(data),
      });
      // Update Mixpanel user properties
      initMixpanel(data);
    },
    onError: (error: any) => {
      trackEvent(MixpanelEvents.PROFILE_UPDATE_FAILED, {
        error_message: error?.response?.data?.detail || "Unknown error",
        error_detail: error?.response?.data,
      });
    },
  });
};

export const useForgotPassword = () => {
  return useMutation({
    mutationFn: (email: string) => apiClient.forgotPassword(email),
  });
};

export const useResetPassword = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({
      token,
      password,
      passwordConfirm,
    }: {
      token: string;
      password: string;
      passwordConfirm: string;
    }) => apiClient.resetPassword(token, password, passwordConfirm),
    onSuccess: () => {
      navigate("/login");
    },
  });
};

export const useVerifyEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (otp: string) => apiClient.verifyEmail(otp),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
};

export const useChangePassword = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: {
      current_password: string;
      new_password: string;
      password_confirm: string;
    }) => apiClient.changePassword(data),
    onSuccess: () => {
      trackEvent(MixpanelEvents.PASSWORD_CHANGED);
      navigate("/");
    },
    onError: (error: any) => {
      trackEvent(MixpanelEvents.PASSWORD_CHANGE_FAILED, {
        error_message: error?.response?.data?.detail || "Unknown error",
        error_detail: error?.response?.data,
      });
    },
  });
};
